(() => {
  if (window.QuadroHomepageSlideshow) return;
  const instances = new Map();
  const setupHomepageSlideshow = (root = document) => {
    const candidates = [
      ...(root.matches?.('.slideshow[data-autorotate]') ? [root] : []),
      ...root.querySelectorAll('.slideshow[data-autorotate]')
    ].filter((slider) => slider.closest('[data-section-type="quadro-slideshow-section"]'));
    candidates.forEach((slider) => {
      if (instances.has(slider) || slider.dataset.homepageSlideshowReady === 'true') return;
      const slides = Array.from(slider.querySelectorAll('.slideshow__slide'));
      if (!slides.length) return;
      const pictures = Array.from(slider.querySelectorAll('[data-slideshow-responsive-picture]'));
      const wrapper = slider.closest('.slideshow-wrapper') || slider;
      const prev = wrapper.querySelector('.slideshow__arrow-left');
      const next = wrapper.querySelector('.slideshow__arrow-right');
      const pause = wrapper.querySelector('.slideshow__pause');
      const arrows = wrapper.querySelector('.slideshow__arrows');
      const pagination = wrapper.querySelector('.slideshow__pagination') || arrows;
      const events = new AbortController();
      const on = (element, name, handler) => element?.addEventListener(name, handler, { signal: events.signal });
      const mediaJobs = new WeakMap();
      const cancelMediaJobs = new Set();
      const failedSlides = new Set();
      const speed = Number(slider.dataset.speed) || 7000;
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let requestedIndex = activeIndex;
      let navigationVersion = 0;
      let automaticNavigation = false;
      let timer = null;
      let disposed = false;
      let isPaused = slider.dataset.autorotate !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let isDragging = false;
      let startX = 0;
      const bounds = slider.getBoundingClientRect();
      let inViewport = bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < innerHeight;
      const visible = () => !disposed && slider.isConnected && inViewport && document.visibilityState !== 'hidden';
      const normalizeIndex = (index) => ((index % slides.length) + slides.length) % slides.length;
      const syncResponsiveSources = () => {
        const shape = document.documentElement.dataset.quadroViewportShape;
        if (!['wide', 'compact', 'tall'].includes(shape)) return;
        pictures.forEach((picture) => {
          picture.querySelectorAll('source[data-slideshow-shape]').forEach((source) => {
            source.media = source.dataset.slideshowShape === shape ? 'all' : 'not all';
          });
        });
      };
      const viewportShapeObserver = typeof MutationObserver === 'function'
        ? new MutationObserver((records) => {
            if (records.some((record) => record.attributeName === 'data-quadro-viewport-shape')) {
              syncResponsiveSources();
            }
          })
        : null;
      viewportShapeObserver?.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-quadro-viewport-shape']
      });
      syncResponsiveSources();
      const dots = document.createElement('ul');
      dots.className = 'quadro-slideshow__dots';
      dots.setAttribute('aria-hidden', 'true');
      slides.forEach(() => {
        const item = document.createElement('li');
        const marker = document.createElement('span');
        marker.className = 'quadro-slideshow__dot';
        item.appendChild(marker);
        dots.appendChild(item);
      });
      if (pagination && slides.length > 1) pagination.insertBefore(dots, pagination.firstChild);
      const dotMarkers = Array.from(dots.querySelectorAll('.quadro-slideshow__dot'));
      const update = () => {
        slides.forEach((slide, index) => {
          const active = index === activeIndex;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          slide.inert = !active;
        });
        dotMarkers.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
        slider.classList.add('is-ready');
      };
      const loadSlideMedia = (slide, priority) => {
        slide.querySelectorAll('[data-slideshow-deferred]').forEach((picture) => {
          // Sources first: let picture select the mobile resource before its fallback.
          picture.querySelectorAll('img').forEach((image) => image.setAttribute('fetchpriority', priority));
          picture.querySelectorAll('source[data-srcset]').forEach((source) => {
            source.srcset = source.dataset.srcset;
            source.removeAttribute('data-srcset');
          });
          picture.querySelectorAll('img[data-src], img[data-srcset]').forEach((image) => {
            if (image.dataset.srcset) {
              image.srcset = image.dataset.srcset;
              image.removeAttribute('data-srcset');
            }
            if (image.dataset.src) {
              image.src = image.dataset.src;
              image.removeAttribute('data-src');
            }
          });
          picture.removeAttribute('data-slideshow-deferred');
        });
        if (priority === 'high') slide.querySelectorAll('.slideshow__picture img').forEach((image) => image.setAttribute('fetchpriority', 'high'));
      };
      const imageReady = (image) => {
        if (mediaJobs.has(image)) return mediaJobs.get(image);
        const job = new Promise((resolve) => {
          let finished = false;
          let decoding = false;
          const finish = (ready) => {
            if (finished) return;
            finished = true;
            clearTimeout(timeout);
            image.removeEventListener('load', check);
            image.removeEventListener('error', failed);
            cancelMediaJobs.delete(cancel);
            resolve(ready);
          };
          const failed = () => finish(false);
          const cancel = () => finish(false);
          const check = () => {
            if (finished || decoding || !image.complete) return;
            // A deferred slide's transparent placeholder is not ready slide media.
            if (!image.currentSrc || image.currentSrc.startsWith('data:')) return;
            if (!image.naturalWidth) return finish(false);
            decoding = true;
            const source = image.currentSrc;
            const decoded = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
            decoded.then(() => {
              decoding = false;
              if (image.currentSrc !== source || !image.complete) return check();
              finish(image.naturalWidth > 0);
            }, () => {
              decoding = false;
              if (image.currentSrc !== source) return check();
              finish(false);
            });
          };
          const timeout = window.setTimeout(failed, 15000);
          cancelMediaJobs.add(cancel);
          image.addEventListener('load', check);
          image.addEventListener('error', failed);
          queueMicrotask(check);
        });
        mediaJobs.set(image, job);
        job.then(() => mediaJobs.delete(image));
        return job;
      };
      const prepareSlide = async (index, priority = 'low') => {
        if (!visible()) return false;
        const slide = slides[normalizeIndex(index)];
        loadSlideMedia(slide, priority);
        return (await Promise.all(Array.from(slide.querySelectorAll('.slideshow__picture img'), imageReady))).every(Boolean);
      };
      const stopTimer = () => {
        if (timer !== null) window.clearTimeout(timer);
        timer = null;
      };
      const nextAvailable = () => {
        for (let step = 1; step < slides.length; step += 1) {
          const index = normalizeIndex(activeIndex + step);
          if (!failedSlides.has(index)) return index;
        }
        return activeIndex;
      };
      const startTimer = () => {
        stopTimer();
        if (!visible() || isPaused || isDragging || slides.length < 2 || requestedIndex !== activeIndex) return;
        const index = nextAvailable();
        if (index !== activeIndex) timer = window.setTimeout(() => goTo(index, true), speed);
      };
      const preloadNeighbours = () => {
        if (!visible() || slides.length < 2) return;
        new Set([normalizeIndex(activeIndex - 1), normalizeIndex(activeIndex + 1)]).forEach((index) => {
          if (index !== activeIndex) prepareSlide(index).then((ready) => {
            if (!disposed && !ready) failedSlides.add(index);
          });
        });
      };
      const primeActive = async () => {
        const index = activeIndex;
        const version = navigationVersion;
        const ready = await prepareSlide(index, 'high');
        if (!visible() || index !== activeIndex || version !== navigationVersion) return;
        if (ready) {
          preloadNeighbours();
        }
        startTimer();
      };
      const goTo = async (index, automatic = false) => {
        requestedIndex = normalizeIndex(index);
        automaticNavigation = automatic;
        const targetIndex = requestedIndex;
        const version = ++navigationVersion;
        stopTimer();
        if (!visible()) return;
        if (targetIndex === activeIndex) return primeActive();
        const ready = await prepareSlide(targetIndex, 'high');
        if (!visible() || version !== navigationVersion) return;
        if (ready) {
          failedSlides.delete(targetIndex);
          delete slider.dataset.slideshowMediaError;
          activeIndex = targetIndex;
          update();
          preloadNeighbours();
        } else {
          // Keep the previous real image visible and the controls usable after an error.
          failedSlides.add(targetIndex);
          slider.dataset.slideshowMediaError = String(targetIndex + 1);
          requestedIndex = activeIndex;
        }
        automaticNavigation = false;
        startTimer();
      };
      [[prev, -1], [next, 1]].forEach(([control, direction]) => {
        on(control, 'click', () => goTo(requestedIndex + direction));
        on(control, 'pointerenter', () => prepareSlide(requestedIndex + direction));
        on(control, 'focus', () => prepareSlide(requestedIndex + direction));
      });
      const updatePause = () => {
        pause?.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
        pause?.classList.toggle('is-paused', isPaused);
      };
      on(pause, 'click', () => {
        isPaused = !isPaused;
        if (isPaused && automaticNavigation) {
          navigationVersion += 1;
          requestedIndex = activeIndex;
          automaticNavigation = false;
        }
        updatePause();
        startTimer();
      });
      on(slider, 'pointerdown', (event) => {
        if (event.button !== 0 || slides.length < 2 || event.target.closest('a.slideshow__btn, button, input, select, textarea')) return;
        isDragging = true;
        startX = event.clientX;
        stopTimer();
        slider.classList.add('is-dragging');
        slider.setPointerCapture(event.pointerId);
      });
      on(slider, 'pointerup', (event) => {
        if (!isDragging) return;
        isDragging = false;
        slider.classList.remove('is-dragging');
        if (slider.hasPointerCapture(event.pointerId)) slider.releasePointerCapture(event.pointerId);
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 40) goTo(requestedIndex + (delta < 0 ? 1 : -1));
        else startTimer();
      });
      on(slider, 'pointercancel', () => {
        isDragging = false;
        slider.classList.remove('is-dragging');
        startTimer();
      });
      const visibilityChanged = () => {
        stopTimer();
        if (!visible()) {
          navigationVersion += 1;
          return;
        }
        if (requestedIndex !== activeIndex) goTo(requestedIndex, automaticNavigation);
        else primeActive();
      };
      const observer = new IntersectionObserver(([entry]) => {
        const changed = inViewport !== entry.isIntersecting;
        inViewport = entry.isIntersecting;
        if (changed) visibilityChanged();
      });
      observer.observe(slider);
      on(document, 'visibilitychange', visibilityChanged);
      const destroy = () => {
        disposed = true;
        navigationVersion += 1;
        stopTimer();
        events.abort();
        observer.disconnect();
        viewportShapeObserver?.disconnect();
        cancelMediaJobs.forEach((cancel) => cancel());
        dots.remove();
        slider.classList.remove('is-dragging');
        delete slider.dataset.homepageSlideshowReady;
        instances.delete(slider);
      };
      instances.set(slider, destroy);
      slider.dataset.homepageSlideshowReady = 'true';
      updatePause();
      update();
      primeActive();
    });
  };
  window.QuadroHomepageSlideshow = { setup: setupHomepageSlideshow };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setupHomepageSlideshow(), { once: true });
  else setupHomepageSlideshow();
  document.addEventListener('shopify:section:load', (event) => setupHomepageSlideshow(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((destroy, slider) => { if (event.target === slider || event.target.contains(slider)) destroy(); });
  });
})();
